import React, { useState } from 'react';
import {
  Table, Button, Drawer, Form, Input, Select, InputNumber, Upload, Space, message,
  Divider, Alert, Modal, Typography, Tag, Tooltip, Popconfirm, Descriptions
} from 'antd';
import {
  PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined,
  UploadOutlined, ExclamationCircleFilled
} from '@ant-design/icons';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_PLATFORM_MERCHANTS = [
  {
    key: 'test-plat-1', name: 'test-鹤梦平台支付宝', channel: 'alipay', rate: 1.5,
    fixedFeeCny: 0.90, fixedFeeUsd: 0.15,
    createTime: '2025-12-12 18:07:45', refApps: [{ name: '鹤梦智能家居', platform: 'iOS', region: '国内' }, { name: '鹤梦智能家居', platform: 'Android', region: '全球' }]
  },
  {
    key: 'test-plat-2', name: 'test-鹤梦平台微信', channel: 'wechat', rate: 0.6,
    fixedFeeCny: 0.30,
    createTime: '2025-12-11 17:52:57', refApps: [{ name: '智能门锁控制', platform: 'Android', region: '国内' }]
  },
  {
    key: 'test-plat-3', name: 'test-平台PayPal商户', channel: 'paypal', rate: 3.5,
    fixedFeeUsd: 0.50,
    createTime: '2025-12-10 15:30:00', refApps: []
  },
  {
    key: 'test-plat-4', name: 'test-平台ApplePay', channel: 'applepay', rate: 2.0,
    createTime: '2025-12-09 10:22:00', refApps: [{ name: '健康助手', platform: 'iOS', region: '海外' }]
  },
  {
    key: 'test-plat-5', name: 'test-平台Stripe', channel: 'stripe', rate: 2.9,
    createTime: '2025-12-08 09:15:00', refApps: []
  },
];

const channelConfig = {
  wechat: { label: '微信', fields: ['appId', 'merchantId', 'privateKeyId', 'privateKeyPath', 'publicKeyId', 'publicKeyPath'] },
  alipay: { label: '支付宝', fields: ['appId', 'publicKey', 'privateKey', 'callbackUrl'] },
  paypal: { label: 'PayPal', fields: ['appId', 'customerId', 'customerPrivateKey'] },
  applepay: { label: 'Apple Pay', fields: ['issuerId', 'publicKeyId', 'publicKeyPath'] },
  googlepay: { label: 'Google Pay', fields: ['customerEmail', 'privateKey'] },
  payssion: { label: 'Payssion', fields: ['apiInterfaceKey', 'encryptionKey'] },
  wechat_mini: { label: '微信小程序', fields: ['apiV3Key', 'platformCertificate', 'merchantId', 'merchantCertSerialNo', 'miniAppId', 'merchantApiPrivateKey'] },
  zarinpal: { label: 'ZARINPAL', fields: ['merchantId'] },
  airwallex: { label: 'AIRWALLEX', fields: ['customerId', 'apiKey'] },
  stripe: { label: 'STRIPE', fields: ['publicKey', 'privateKey'] },
  fuyou: { label: '富友支付', fields: ['merchantId', 'privateKey', 'orderPrefix', 'wechatAppId'] },
};

const fieldLabels = {
  appId: 'APP ID', merchantId: '商户ID', privateKeyId: '私钥ID', privateKeyPath: '私钥路径',
  publicKeyId: '公钥ID', publicKeyPath: '公钥路径', publicKey: '公钥', privateKey: '密钥',
  customerId: '客户ID', customerPrivateKey: '客户私钥', issuerId: 'issuerld', customerEmail: '客户邮箱',
  apiInterfaceKey: '应用接口密钥', encryptionKey: '加密密钥', apiV3Key: 'API v3密钥',
  merchantApiPrivateKey: '商户API私钥', platformCertificate: '平台证书', miniAppId: '小程序应用id',
  merchantCertSerialNo: '商户证书序列号', apiKey: 'API密钥', orderPrefix: '订单前缀',
  wechatAppId: '微信APPID', callbackUrl: '回调地址',
};

// 拼接支付费率展示文案：5% + 0.5USD/0.9CNY
const formatRateDisplay = (rate, fixedFeeUsd, fixedFeeCny) => {
  const parts = [`${rate}%`];
  const feeParts = [];
  if (fixedFeeUsd !== undefined && fixedFeeUsd !== null) {
    feeParts.push(`${fixedFeeUsd}USD`);
  }
  if (fixedFeeCny !== undefined && fixedFeeCny !== null) {
    feeParts.push(`${fixedFeeCny}CNY`);
  }
  if (feeParts.length > 0) {
    parts.push(feeParts.join('/'));
  }
  return parts.join(' + ');
};

const PlatformMerchant = () => {
  const [form] = Form.useForm();
  const [merchants, setMerchants] = useState(MOCK_PLATFORM_MERCHANTS);
  const [filteredMerchants, setFilteredMerchants] = useState(MOCK_PLATFORM_MERCHANTS);
  const [searchName, setSearchName] = useState('');
  const [searchChannel, setSearchChannel] = useState(undefined);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [detailVisible, setDetailVisible] = useState(false);
  const [editingMerchant, setEditingMerchant] = useState(null);
  const [selectedMerchant, setSelectedMerchant] = useState(null);
  const [selectedChannel, setSelectedChannel] = useState(null);

  // 平台级已使用的渠道
  const usedChannels = new Set(merchants.map(m => m.channel));

  const handleSearch = () => {
    let data = merchants;
    if (searchName) {
      data = data.filter(m => m.name.toLowerCase().includes(searchName.toLowerCase()));
    }
    if (searchChannel) {
      data = data.filter(m => m.channel === searchChannel);
    }
    setFilteredMerchants(data);
  };

  const handleReset = () => {
    setSearchName('');
    setSearchChannel(undefined);
    setFilteredMerchants(merchants);
  };

  // 获取可用渠道（排除已配置的）
  const getAvailableChannels = (excludeKey) => {
    return Object.entries(channelConfig).map(([key, config]) => ({
      key, label: config.label,
      disabled: key !== excludeKey && usedChannels.has(key),
      reason: key !== excludeKey && usedChannels.has(key) ? '该支付渠道已配置' : null,
    }));
  };

  const handleAdd = () => {
    setEditingMerchant(null);
    setSelectedChannel(null);
    form.resetFields();
    setDrawerVisible(true);
  };

  const handleEdit = (record) => {
    setEditingMerchant(record);
    setSelectedChannel(record.channel);
    form.setFieldsValue(record);
    setDrawerVisible(true);
  };

  const handleDeleteClick = (record) => {
    const refCount = record.refApps?.length || 0;
    Modal.confirm({
      title: '删除商户号',
      icon: <ExclamationCircleFilled />,
      content: refCount > 0
        ? `当前有 ${refCount} 个 App 在使用，确定要删除吗？删除后将影响 App 的支付功能。`
        : '确定要删除该商户号吗？此操作不可恢复。删除后将影响 App 的支付功能。',
      okText: '确定删除',
      okType: 'danger',
      cancelText: '取消',
      onOk() {
        const updated = merchants.filter(m => m.key !== record.key);
        setMerchants(updated);
        setFilteredMerchants(updated);
        message.success('删除成功');
      },
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      if (editingMerchant) {
        const updated = merchants.map(m => m.key === editingMerchant.key ? { ...m, ...values } : m);
        setMerchants(updated);
        setFilteredMerchants(updated);
        message.success('保存成功');
      } else {
        const newMerchant = {
          key: Date.now().toString(),
          ...values,
          createTime: new Date().toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }),
          refApps: [],
        };
        const updated = [...merchants, newMerchant];
        setMerchants(updated);
        setFilteredMerchants(updated);
        message.success('创建成功');
      }
      setDrawerVisible(false);
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleDetail = (record) => {
    setSelectedMerchant(record);
    setDetailVisible(true);
  };

  const handleChannelChange = (value) => {
    setSelectedChannel(value);
    form.setFieldsValue({ channel: value });
  };

  const handleRefAppsClick = (record) => {
    setSelectedMerchant(record);
    setDetailVisible(true);
  };

  const renderFormFields = () => {
    if (!selectedChannel) return null;
    const config = channelConfig[selectedChannel];
    if (!config) return null;
    return config.fields.map(field => (
      <Form.Item key={field} label={fieldLabels[field]} name={field}>
        {field.includes('Path') || field === 'platformCertificate' ? (
          <Upload maxCount={1}><Button icon={<UploadOutlined />}>上传文件</Button></Upload>
        ) : field.includes('Key') || field === 'privateKey' || field === 'publicKey' ? (
          <Input.TextArea placeholder="请输入" rows={3} />
        ) : (
          <Input placeholder="请输入" />
        )}
      </Form.Item>
    ));
  };

  // 所有渠道均已配置
  const allChannelsUsed = Object.keys(channelConfig).every(ch => usedChannels.has(ch));

  const columns = [
    { title: '序号', key: 'index', width: 70, render: (_, __, index) => index + 1 },
    { title: '商户名称', dataIndex: 'name', key: 'name', width: 220 },
    {
      title: '支付渠道', dataIndex: 'channel', key: 'channel', width: 120,
      render: (channel) => <Tag color="blue" style={{ border: 'none' }}>{channelConfig[channel]?.label || channel}</Tag>,
    },
    {
      title: '支付费率', dataIndex: 'rate', key: 'rate', width: 180,
      render: (rate, record) => <Text strong>{formatRateDisplay(rate, record.fixedFeeUsd, record.fixedFeeCny)}</Text>,
    },
    {
      title: '已引用 App 数', dataIndex: 'refApps', key: 'refApps', width: 130,
      render: (refApps) => {
        const count = refApps?.length || 0;
        if (count === 0) {
          return <Text type="secondary">0</Text>;
        }
        return (
          <Button type="link" size="small" onClick={() => {
            const record = merchants.find(m => m.refApps === refApps);
            if (record) handleRefAppsClick(record);
          }}>
            <Text strong style={{ color: '#1890ff' }}>{count}</Text>
          </Button>
        );
      },
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
    {
      title: '操作', key: 'action', width: 220, fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => handleDetail(record)}>详细信息</Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
          <Popconfirm
            title="删除后不可恢复，确定删除？"
            onConfirm={() => handleDeleteClick(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />}>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Title level={4} style={{ marginBottom: 16 }}>平台商户号</Title>

      <Alert
        message="管理平台级商户号资源，用于后续分配给企业的 App 收款。每个支付渠道只允许配置一组平台商户号。"
        type="info"
        showIcon
        closable
        style={{ marginBottom: 16, borderRadius: 8 }}
      />

      {/* 搜索区 */}
      <div style={{ padding: '16px 24px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', marginBottom: 8 }}>
        <Space wrap>
          <Input
            placeholder="商户名称"
            value={searchName}
            onChange={e => setSearchName(e.target.value)}
            style={{ width: 200 }}
            allowClear
          />
          <Select
            placeholder="支付渠道"
            value={searchChannel}
            onChange={setSearchChannel}
            style={{ width: 150 }}
            allowClear
          >
            {Object.entries(channelConfig).map(([key, config]) => (
              <Select.Option key={key} value={key}>{config.label}</Select.Option>
            ))}
          </Select>
          <Button type="primary" onClick={handleSearch}>查询</Button>
          <Button onClick={handleReset}>重置</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} style={{ marginLeft: 16 }}>
            创建商户号
          </Button>
        </Space>
      </div>

      {/* 表格 */}
      <Table
        columns={columns}
        dataSource={filteredMerchants}
        pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` }}
        size="small"
        scroll={{ x: 1050 }}
        locale={{ emptyText: (
          <div style={{ padding: '40px 0' }}>
            <div style={{ fontSize: 14, color: '#999', marginBottom: 16 }}>暂无平台商户号</div>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>创建商户号</Button>
          </div>
        )}}
      />

      {/* 新建/编辑 Drawer */}
      <Drawer
        title={editingMerchant ? '编辑商户号' : '创建商户号'}
        placement="right"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        width={680}
        footer={
          <div style={{ textAlign: 'right' }}>
            <Button onClick={() => setDrawerVisible(false)} style={{ marginRight: 8 }}>取消</Button>
            <Button type="primary" onClick={handleSave}>保存</Button>
          </div>
        }
      >
        {allChannelsUsed && !editingMerchant && (
          <Alert
            message="当前范围下所有支付渠道均已配置，无法创建新商户号"
            type="warning"
            showIcon
            style={{ marginBottom: 24, borderRadius: 8 }}
          />
        )}

        <Form form={form} layout="vertical">
          <Form.Item label="商户名称" name="name" rules={[{ required: true, message: '请输入商户名称' }]}>
            <Input placeholder="请输入商户名称" />
          </Form.Item>

          <Form.Item label="支付渠道" name="channel" rules={[{ required: true, message: '请选择支付渠道' }]}>
            <Select placeholder="请选择" onChange={handleChannelChange}>
              {getAvailableChannels(editingMerchant?.channel).map(({ key, label, disabled, reason }) => (
                <Select.Option key={key} value={key} disabled={disabled}>
                  <Tooltip title={reason} placement="right">
                    <span>{label}</span>
                  </Tooltip>
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="支付费率" name="rate" rules={[{ required: true, message: '请输入支付费率' }]}>
            <InputNumber min={0.01} max={100} step={0.01} style={{ width: '100%' }} placeholder="0.00" suffix="%" />
          </Form.Item>

          <div style={{ fontSize: 13, fontWeight: 500, color: 'rgba(0,0,0,0.45)', margin: '12px 0 8px' }}>
            额外固定扣除金额
          </div>
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            <Form.Item label="CNY" name="fixedFeeCny" style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} placeholder="0.00" />
            </Form.Item>
            <Form.Item label="USD" name="fixedFeeUsd" style={{ flex: 1, marginBottom: 0 }}>
              <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} placeholder="0.00" />
            </Form.Item>
          </div>

          <Divider />

          {renderFormFields()}
        </Form>
      </Drawer>

      {/* 详情 Drawer */}
      <Drawer
        title="商户号详情"
        placement="right"
        onClose={() => setDetailVisible(false)}
        open={detailVisible}
        width={600}
      >
        {selectedMerchant && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <Descriptions bordered column={1} size="small">
                <Descriptions.Item label="商户名称">{selectedMerchant.name}</Descriptions.Item>
                <Descriptions.Item label="支付渠道">
                  <Tag color="blue" style={{ border: 'none' }}>{channelConfig[selectedMerchant.channel]?.label}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="支付费率"><Text strong>{formatRateDisplay(selectedMerchant.rate, selectedMerchant.fixedFeeUsd, selectedMerchant.fixedFeeCny)}</Text></Descriptions.Item>
                <Descriptions.Item label="创建时间">{selectedMerchant.createTime}</Descriptions.Item>
              </Descriptions>
            </div>

            <div style={{ fontWeight: 500, marginBottom: 12 }}>渠道配置</div>
            {channelConfig[selectedMerchant.channel]?.fields.map(field => (
              <div key={field} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f0f0f0', fontSize: 13 }}>
                <Text type="secondary">{fieldLabels[field]}</Text>
                <Text style={{ maxWidth: '55%', textAlign: 'right', wordBreak: 'break-all' }}>
                  {selectedMerchant[field] || <span style={{ color: '#ccc' }}>-</span>}
                </Text>
              </div>
            ))}

            <Divider />
            <div style={{ fontWeight: 500, marginBottom: 12 }}>引用 App 列表</div>
            {selectedMerchant.refApps?.length > 0 ? (
              <Table
                dataSource={selectedMerchant.refApps.map((app, i) => ({ ...app, key: i }))}
                columns={[
                  { title: 'App 名称', dataIndex: 'name', key: 'name' },
                  { title: '系统平台 - 区域', key: 'platform', render: (_, r) => `${r.platform || '--'} - ${r.region || '--'}` },
                ]}
                pagination={false}
                size="small"
              />
            ) : (
              <Text type="secondary">暂无 App 引用</Text>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default PlatformMerchant;
